import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Plus,
  Loader2,
  AlertCircle,
  Edit2,
  Trash2,
  Search,
  UserCheck,
  UserX,
  Building2,
} from 'lucide-react';
import api, { extractError } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { User, PaginatedResponse, Organization } from '../types';
import { UserRole } from '../types';

const roleLabels: Record<UserRole, string> = {
  [UserRole.ADMIN_GENERALSEKRETARIAT]: 'Admin GS',
  [UserRole.ADMIN_LANDESVERBAND]: 'Admin LV',
  [UserRole.ADMIN_BEREICH]: 'Admin Bereich',
  [UserRole.ADMIN_BEZIRKSSTELLE]: 'Admin Bez.St.',
  [UserRole.USER_EDITOR]: 'Editor',
  [UserRole.USER_VIEWER]: 'Viewer',
  [UserRole.USER_INPUT]: 'Eingabe',
};

const roleColors: Record<UserRole, string> = {
  [UserRole.ADMIN_GENERALSEKRETARIAT]: 'bg-red-100 text-red-800',
  [UserRole.ADMIN_LANDESVERBAND]: 'bg-blue-100 text-blue-800',
  [UserRole.ADMIN_BEREICH]: 'bg-teal-100 text-teal-800',
  [UserRole.ADMIN_BEZIRKSSTELLE]: 'bg-green-100 text-green-800',
  [UserRole.USER_EDITOR]: 'bg-purple-100 text-purple-800',
  [UserRole.USER_VIEWER]: 'bg-gray-100 text-gray-800',
  [UserRole.USER_INPUT]: 'bg-yellow-100 text-yellow-800',
};

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editUser?: User | null;
}

function UserFormModal({ isOpen, onClose, editUser }: UserFormModalProps) {
  const { user: currentUser, updateAuthUser } = useAuth();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    username: editUser?.username ?? '',
    password: '',
    firstName: editUser?.firstName ?? '',
    lastName: editUser?.lastName ?? '',
    email: editUser?.email ?? '',
    role: editUser?.role ?? UserRole.USER_VIEWER,
    organizationId: editUser?.organizationId ?? '',
    enabled: editUser?.enabled ?? true,
    alarmplanEnabled: editUser?.alarmplanEnabled ?? false,
  });
  const [error, setError] = useState<string | null>(null);
  const prevEditUserRef = useRef(editUser);

  const { data: orgsData } = useQuery<PaginatedResponse<Organization>>({
    queryKey: ['organizations'],
    queryFn: async () => {
      const res = await api.get('/organizations', { params: { limit: 100 } });
      const items = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      return { data: items, total: items.length, page: 1, limit: 100, totalPages: 1 };
    },
  });

  useEffect(() => {
    if (editUser !== prevEditUserRef.current) {
      setFormData({
        username: editUser?.username ?? '',
        password: '',
        firstName: editUser?.firstName ?? '',
        lastName: editUser?.lastName ?? '',
        email: editUser?.email ?? '',
        role: editUser?.role ?? UserRole.USER_VIEWER,
        organizationId: editUser?.organizationId ?? '',
        enabled: editUser?.enabled ?? true,
        alarmplanEnabled: editUser?.alarmplanEnabled ?? false,
      });
      setError(null);
      prevEditUserRef.current = editUser;
    }
  }, [editUser]);

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const computedDisplayName = [data.firstName, data.lastName].filter(Boolean).join(' ').trim();
      const payload: Record<string, unknown> = {
        username: data.username,
        firstName: data.firstName || undefined,
        lastName: data.lastName || undefined,
        displayName: computedDisplayName || undefined,
        email: data.email || undefined,
        role: data.role,
        organizationId: data.organizationId || undefined,
        enabled: data.enabled,
        alarmplanEnabled: data.alarmplanEnabled,
      };
      if (data.password) {
        payload.password = data.password;
      }
      const res = await api.post('/users', payload);
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      if (currentUser && data?.id === currentUser.id) updateAuthUser(data);
      onClose();
    },
    onError: (err) => setError(extractError(err).message),
  });

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const computedDisplayName = [data.firstName, data.lastName].filter(Boolean).join(' ').trim();
      const payload: Record<string, unknown> = {
        firstName: data.firstName || undefined,
        lastName: data.lastName || undefined,
        displayName: computedDisplayName || undefined,
        email: data.email || undefined,
        role: data.role,
        organizationId: data.organizationId || undefined,
        enabled: data.enabled,
        alarmplanEnabled: data.alarmplanEnabled,
      };
      if (data.password) {
        payload.password = data.password;
      }
      const res = await api.patch(`/users/${editUser!.id}`, payload);
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      if (currentUser && data?.id === currentUser.id) updateAuthUser(data);
      onClose();
    },
    onError: (err) => setError(extractError(err).message),
  });

  if (!isOpen) return null;

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!formData.username.trim()) {
      setError('Benutzername ist erforderlich');
      return;
    }
    if (!editUser && !formData.password) {
      setError('Passwort ist für neue Benutzer erforderlich');
      return;
    }

    if (editUser) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {editUser ? 'Benutzer bearbeiten' : 'Neuen Benutzer anlegen'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={18} className="shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label htmlFor="user-username" className="label">Benutzername</label>
            <input
              id="user-username"
              type="text"
              className="input-field"
              required
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              disabled={!!editUser}
            />
          </div>

          <div>
            <label htmlFor="user-password" className="label">
              {editUser ? 'Neues Passwort (leer lassen zum Beibehalten)' : 'Passwort'}
            </label>
            <input
              id="user-password"
              type="password"
              className="input-field"
              required={!editUser}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="user-firstname" className="label">Vorname</label>
              <input
                id="user-firstname"
                type="text"
                className="input-field"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="user-lastname" className="label">Nachname</label>
              <input
                id="user-lastname"
                type="text"
                className="input-field"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              />
            </div>
          </div>
          {(formData.firstName || formData.lastName) && (
            <p className="text-xs text-gray-400 -mt-3">
              Anzeigename: <strong>{[formData.firstName, formData.lastName].filter(Boolean).join(' ')}</strong>
            </p>
          )}

          <div>
            <label htmlFor="user-email" className="label">E-Mail (optional)</label>
            <input
              id="user-email"
              type="email"
              className="input-field"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="user-role" className="label">Rolle</label>
            <select
              id="user-role"
              className="input-field"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
            >
              {Object.entries(roleLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="user-org" className="label">Organisation</label>
            <select
              id="user-org"
              className="input-field"
              value={formData.organizationId}
              onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}
            >
              <option value="">Keine</option>
              {orgsData?.data.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name} ({org.level})
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <input
              id="user-enabled"
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
            />
            <label htmlFor="user-enabled" className="text-sm text-gray-700">
              Benutzer aktiv
            </label>
          </div>

          <div className="flex items-center gap-3">
            <input
              id="user-alarmplan"
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              checked={formData.alarmplanEnabled}
              onChange={(e) => setFormData({ ...formData, alarmplanEnabled: e.target.checked })}
            />
            <label htmlFor="user-alarmplan" className="text-sm text-gray-700">
              Alarmplan-Zugriff
            </label>
          </div>

          {editUser && (
            <div className="border-t border-gray-100 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Zwei-Faktor-Authentifizierung (MFA)</h3>
              <MfaSection userId={editUser.id} />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Abbrechen
            </button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? (
                <><Loader2 size={16} className="animate-spin" /> Speichern...</>
              ) : (
                editUser ? 'Aktualisieren' : 'Erstellen'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ─── MFA Section ───────────────────────────────────

function MfaSection({ userId }: { userId: string }) {
  const [mfaStatus, setMfaStatus] = useState<any>(null);
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const loadMfa = () => {
    api.get('/users/' + userId).then(res => {
      setMfaStatus({ enabled: res.data?.mfaEnabled, secret: res.data?.mfaSecret });
    }).catch(() => {});
  };

  useEffect(() => { if (userId) loadMfa(); }, [userId]);

  async function setupMfa() {
    setLoading(true);
    try {
      const res = await api.post('/users/' + userId + '/mfa/setup');
      setMfaStatus({ enabled: false, secret: res.data.secret, otpauthUrl: res.data.otpauthUrl });
      setMsg('Secret generiert. Scanne den QR-Code oder gib das Secret manuell ein.');
    } catch (e: any) { setMsg('Fehler: ' + (e.response?.data?.message || e.message)); }
    setLoading(false);
  }

  async function enableMfa() {
    if (!token) return;
    setLoading(true);
    try {
      await api.post('/users/' + userId + '/mfa/enable', { token });
      setMsg('MFA aktiviert!');
      setToken('');
      loadMfa();
    } catch (e: any) { setMsg('Fehler: ' + (e.response?.data?.message || e.message)); }
    setLoading(false);
  }

  async function disableMfa() {
    if (!confirm('MFA wirklich deaktivieren?')) return;
    setLoading(true);
    try {
      await api.post('/users/' + userId + '/mfa/disable');
      setMfaStatus({ enabled: false, secret: null });
      setMsg('MFA deaktiviert.');
    } catch (e: any) { setMsg('Fehler: ' + (e.response?.data?.message || e.message)); }
    setLoading(false);
  }

  return (
    <div className="space-y-3 text-sm">
      {mfaStatus?.enabled ? (
        <div className="flex items-center gap-2">
          <span className="text-green-600 font-medium">MFA aktiv</span>
          <button onClick={disableMfa} disabled={loading} className="text-xs text-red-500 hover:underline">Deaktivieren</button>
        </div>
      ) : mfaStatus?.secret ? (
        <div className="space-y-2">
          <p className="text-amber-600">MFA eingerichtet, aber nicht aktiviert.</p>
          <p className="text-xs text-gray-500 break-all">Secret: {mfaStatus.secret}</p>
          <div className="flex gap-2">
            <input className="input-field text-xs w-24" value={token} onChange={e => setToken(e.target.value)} placeholder="6-stelliger Code" maxLength={6} />
            <button onClick={enableMfa} disabled={loading || token.length !== 6} className="btn-primary text-xs">Aktivieren</button>
          </div>
        </div>
      ) : (
        <button onClick={setupMfa} disabled={loading} className="btn-secondary text-xs">
          {loading ? 'Generiere...' : 'MFA einrichten'}
        </button>
      )}
      {msg && <p className="text-xs text-gray-500">{msg}</p>}
    </div>
  );
}

export default function UsersPage() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, error } = useQuery<PaginatedResponse<User>>({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await api.get('/users', { params: { limit: 200 } });
      const items = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      return { data: items, total: items.length, page: 1, limit: 200, totalPages: 1 };
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      await api.patch(`/users/${id}`, { enabled });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/users/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const filteredUsers = data?.data?.filter((u) =>
    u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.displayName ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.email ?? '').toLowerCase().includes(searchQuery.toLowerCase()),
  ) ?? [];

  const handleEdit = (user: User) => {
    setEditUser(user);
    setShowModal(true);
  };

  const handleToggle = (user: User) => {
    const newState = !user.enabled;
    if (window.confirm(`Benutzer "${user.username}" ${newState ? 'aktivieren' : 'deaktivieren'}?`)) {
      toggleMutation.mutate({ id: user.id, enabled: newState });
    }
  };

  const handleDelete = (user: User) => {
    if (window.confirm(`Benutzer "${user.username}" wirklich löschen?`)) {
      deleteMutation.mutate(user.id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditUser(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users size={28} className="text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Benutzer</h1>
            <p className="text-sm text-gray-500">Benutzer verwalten, Rollen zuweisen und aktivieren/deaktivieren</p>
          </div>
        </div>
        <button
          onClick={() => {
            setEditUser(null);
            setShowModal(true);
          }}
          className="btn-primary"
        >
          <Plus size={18} />
          Neuer Benutzer
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Benutzer suchen..."
          className="input-field pl-10"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={18} className="shrink-0" />
          <span>Benutzer konnten nicht geladen werden</span>
        </div>
      )}

      {/* Users Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="py-12 text-center">
            <Users size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">
              {searchQuery ? 'Keine Benutzer gefunden' : 'Noch keine Benutzer vorhanden'}
            </p>
            {!searchQuery && (
              <button onClick={() => setShowModal(true)} className="btn-primary mt-4">
                <Plus size={18} /> Ersten Benutzer anlegen
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Benutzer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Rolle</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Organisation</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user.displayName || user.username}</p>
                        <p className="text-xs text-gray-500">{user.username}{user.email ? ` · ${user.email}` : ''}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`badge text-xs ${roleColors[user.role] || 'bg-gray-100 text-gray-700'}`}>
                        {roleLabels[user.role] || user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {user.organization ? (
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <Building2 size={14} className="text-gray-400" />
                          <span>{user.organization.name}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {user.enabled ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-700">
                          <UserCheck size={14} /> Aktiv
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-red-600">
                          <UserX size={14} /> Deaktiviert
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleToggle(user)}
                          className="btn-ghost p-1.5"
                          title={user.enabled ? 'Deaktivieren' : 'Aktivieren'}
                        >
                          {user.enabled ? <UserX size={14} /> : <UserCheck size={14} />}
                        </button>
                        <button
                          onClick={() => handleEdit(user)}
                          className="btn-ghost p-1.5"
                          title="Bearbeiten"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          className="btn-ghost p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Löschen"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      <UserFormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        editUser={editUser}
      />
    </div>
  );
}
